import { memo } from 'react';
import { FitType } from '@goldcast/player-config/src/types/asset-types';
import { useClipFitFill } from '../useClipFitFill';
import DisabledOptionTooltips from './DisabledOptionTooltips';
import SelectableOptionCard from './SelectableOptionCard';
import ConfirmationDialog from '@/components/organisms/ConfirmationDialog';
import Icon from '@/components/atoms/Icon';
import { classnames } from '@/libs/utils';

const FIT_FILL_CARD_OPTIONS: { value: FitType; label: string }[] = [
  { value: 'FILL', label: 'Fill' },
  { value: 'FIT', label: 'Fit' }
];

const TILE_BG_BY_INDEX = ['bg-slate-200', 'bg-slate-300', 'bg-slate-300', 'bg-slate-200'];

/**
 * 2x2 grid of avatar tiles. "Fill" tiles are edge-to-edge (no gap/padding);
 * "Fit" tiles keep a visible gap/inset so the icon reads as letterboxed.
 * Matches the Speaker cards thumbnails from Figma
 */
function FitFillThumbnail({ fitOption }: { fitOption: FitType }) {
  const isFill = fitOption === 'FILL';
  return (
    <div
      className={classnames(
        'grid h-14 w-full grid-cols-2 overflow-hidden rounded-[6px] bg-slate-100',
        isFill ? 'gap-0 p-0' : 'gap-[3px] p-[3px]'
      )}
    >
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className={classnames('flex items-center justify-center', TILE_BG_BY_INDEX[index])}>
          <Icon name="IconUserSquare" size={16} className="text-slate-400" />
        </div>
      ))}
    </div>
  );
}

function FitFillOption({ fitOption, label, disabled }: { fitOption: FitType; label: string; disabled: boolean }) {
  const { isOpen, changeFitMode, handleConfirm, handleCancel, hasMultipleScenes, clipFitMode } = useClipFitFill({
    fitOption
  });
  const isActive = !hasMultipleScenes && clipFitMode === fitOption;

  return (
    <>
      <SelectableOptionCard
        isActive={isActive}
        disabled={disabled}
        onClick={changeFitMode}
        trackingId={`customizer-fit-fill-${fitOption}`}
        ariaLabel={label}
        tooltipAnchorId={`fit-fill-card-${fitOption}`}
        label={label}
      >
        <FitFillThumbnail fitOption={fitOption} />
      </SelectableOptionCard>
      <ConfirmationDialog
        isOpen={isOpen}
        title="Confirm Fit Change"
        content="This will update all existing scenes with your current selection. Do you want to continue?"
        confirmLabel="Continue"
        cancelLabel="Cancel"
        onClose={handleCancel}
        onConfirm={handleConfirm}
      />
    </>
  );
}

function FitFillCardSelector({
  disabled = false,
  disabledReason = ''
}: {
  disabled?: boolean;
  disabledReason?: string;
}) {
  return (
    <div className="flex w-full flex-col gap-3">
      <div className="text-xs">Speaker cards</div>
      <div className="grid grid-cols-2 gap-x-2.5" role="radiogroup" aria-label="Speaker cards">
        {FIT_FILL_CARD_OPTIONS.map(option => (
          <FitFillOption key={option.value} fitOption={option.value} label={option.label} disabled={disabled} />
        ))}
      </div>
      <DisabledOptionTooltips
        place="top"
        options={
          disabled
            ? FIT_FILL_CARD_OPTIONS.map(option => ({
                id: `fit-fill-card-${option.value}`,
                disabledReason
              }))
            : []
        }
      />
    </div>
  );
}

export default memo(FitFillCardSelector);
